import { useEffect, useRef, useState } from "react";
import { Stage, Layer, Text, Rect, Image as KonvaImage, Transformer } from "react-konva";
import { CanvasWrapper } from "../style";

const CANVAS_W = 1080;
const CANVAS_H = 1920;

/** Map stored font value to CSS font-family name */
const FONT_FAMILY_MAP = {
  NotoSansHebrew: "Noto Sans Hebrew",
};
function cssFont(font) {
  return FONT_FAMILY_MAP[font] || font || "Heebo";
}

// Auto-detect paragraph direction by the first strong character. Hebrew/Arabic
// → RTL; Latin → LTR. Keeps neutral chars (`:`, digits, punctuation) anchored to
// the surrounding text instead of jumping to the opposite visual side.
const RTL_STRONG = /[\p{Script=Hebrew}\p{Script=Arabic}\p{Script=Syriac}\p{Script=Thaana}]/u;
const LTR_STRONG = /[\p{Script=Latin}\p{Script=Greek}\p{Script=Cyrillic}]/u;
function detectDir(text) {
  if (!text) return "ltr";
  for (const ch of text) {
    if (RTL_STRONG.test(ch)) return "rtl";
    if (LTR_STRONG.test(ch)) return "ltr";
  }
  return "ltr";
}

/** Convert direction angle (degrees) to start/end points within a w×h box */
function directionToPoints(deg, w, h) {
  const rad = (deg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return {
    startPoint: { x: (0.5 - cos / 2) * w, y: (0.5 - sin / 2) * h },
    endPoint: { x: (0.5 + cos / 2) * w, y: (0.5 + sin / 2) * h },
  };
}

/** Flatten gradient stops → Konva colorStops [pos, color, ...]. Stops must be
 *  sorted by position or Konva renders banded/wrong transitions. */
function flattenStops(stops) {
  const sorted = [...stops].sort((a, b) => a.position - b.position);
  const result = [];
  for (const s of sorted) {
    result.push(s.position);
    const hex = s.color || "#000000";
    const r = parseInt(hex.slice(1, 3), 16) || 0;
    const g = parseInt(hex.slice(3, 5), 16) || 0;
    const b = parseInt(hex.slice(5, 7), 16) || 0;
    result.push(`rgba(${r},${g},${b},${s.alpha ?? 1})`);
  }
  return result;
}

function useLoadedImage(src) {
  const [img, setImg] = useState(null);
  useEffect(() => {
    if (!src) { setImg(null); return; }
    const image = new window.Image();
    image.src = src;
    image.onload = () => setImg(image);
    image.onerror = () => setImg(null);
  }, [src]);
  return img;
}

export default function CanvasStage({ elements, selectedId, dispatch, containerRef }) {
  const [scale, setScale] = useState(1);
  const transformerRef = useRef(null);
  const stageRef = useRef(null);
  const selectedElement = elements.find((el) => el.id === selectedId) || null;

  // Compute scale to fit the container
  useEffect(() => {
    const updateScale = () => {
      if (!containerRef?.current) return;
      const { clientWidth, clientHeight } = containerRef.current;
      const scaleX = (clientWidth - 32) / CANVAS_W;
      const scaleY = (clientHeight - 32) / CANVAS_H;
      setScale(Math.min(scaleX, scaleY, 1));
    };
    updateScale();
    const ro = new ResizeObserver(updateScale);
    if (containerRef?.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [containerRef]);

  // Attach transformer to selected node
  useEffect(() => {
    if (!transformerRef.current || !stageRef.current) return;
    const node = selectedId ? stageRef.current.findOne(`#${selectedId}`) : null;
    transformerRef.current.nodes(node ? [node] : []);
    transformerRef.current.getLayer()?.batchDraw();
  }, [selectedId, elements]);

  const handleDragEnd = (el) => (e) => {
    dispatch({ type: "UPDATE", id: el.id, patch: { x: Math.round(e.target.x()), y: Math.round(e.target.y()) } });
  };

  const handleTransformEnd = (el) => (e) => {
    const node = e.target;
    dispatch({
      type: "UPDATE",
      id: el.id,
      patch: {
        x: Math.round(node.x()),
        y: Math.round(node.y()),
        w: Math.round(node.width() * node.scaleX()),
        h: Math.round(node.height() * node.scaleY()),
        rotation: Math.round(node.rotation()),
      },
    });
    node.scaleX(1);
    node.scaleY(1);
  };

  // Gradient elements are non-listening unless selected, so they don't intercept
  // clicks meant for elements layered above/below them. Select gradients via the
  // layers panel.
  const isBlocking = (el) =>
    el.type === "gradient" ||
    (el.type === "shape" && (el.alpha ?? 1) < 0.5 && el.w * el.h > 0.4 * CANVAS_W * CANVAS_H);

  const commonProps = (el) => ({
    id: el.id,
    draggable: el.id === selectedId,
    listening: el.id === selectedId || !isBlocking(el),
    onClick: () => dispatch({ type: "SELECT", id: el.id }),
    onTap: () => dispatch({ type: "SELECT", id: el.id }),
    onDragEnd: handleDragEnd(el),
    onTransformEnd: handleTransformEnd(el),
  });

  // Sort by z ascending so higher z draws on top
  const sorted = [...elements].sort((a, b) => a.z - b.z).filter((el) => !el._hidden);

  return (
    <CanvasWrapper style={{ width: CANVAS_W * scale, height: CANVAS_H * scale }}>
      <Stage
        ref={stageRef}
        width={CANVAS_W * scale}
        height={CANVAS_H * scale}
        scaleX={scale}
        scaleY={scale}
        onClick={(e) => {
          if (e.target === e.target.getStage()) dispatch({ type: "SELECT", id: null });
        }}
      >
        {/* Background layer (non-interactive) */}
        <Layer listening={false}>
          <Rect x={0} y={0} width={CANVAS_W} height={CANVAS_H} fill="#1a1a1a" />
        </Layer>

        <Layer>
          {sorted.map((el) => {
            if (el.type === "text") {
              const textValue = el.defaultText || el.label || "Text";
              const dir = detectDir(textValue);
              return (
                <Text
                  key={el.id}
                  {...commonProps(el)}
                  x={el.x}
                  y={el.y}
                  width={el.w}
                  height={el.h}
                  rotation={el.rotation ?? 0}
                  opacity={el.opacity ?? 1}
                  text={textValue}
                  fontFamily={cssFont(el.font)}
                  fontSize={el.size || 48}
                  fontStyle={el.weight === 700 ? "bold" : "normal"}
                  fill={el.color || "#ffffff"}
                  align={el.align === "end" ? "right" : el.align === "center" ? "center" : "left"}
                  direction={dir}
                  wrap="word"
                  shadowColor={el.shadow?.color}
                  shadowOffsetX={el.shadow?.offsetX}
                  shadowOffsetY={el.shadow?.offsetY}
                  shadowBlur={el.shadow?.blur}
                  strokeWidth={el.outline?.width || 0}
                  stroke={el.outline?.color}
                />
              );
            }

            if (el.type === "logo") {
              return <LogoNode key={el.id} el={el} commonProps={commonProps(el)} />;
            }

            if (el.type === "gradient") {
              if (!el.stops || el.stops.length < 2) {
                return (
                  <Rect key={el.id} {...commonProps(el)} x={el.x} y={el.y} width={el.w} height={el.h}
                    rotation={el.rotation ?? 0} opacity={el.opacity ?? 1} fill="#888888" />
                );
              }
              const { startPoint, endPoint } = directionToPoints(el.direction ?? 90, el.w, el.h);
              return (
                <Rect
                  key={el.id}
                  {...commonProps(el)}
                  x={el.x}
                  y={el.y}
                  width={el.w}
                  height={el.h}
                  rotation={el.rotation ?? 0}
                  opacity={el.opacity ?? 1}
                  fillLinearGradientStartPoint={startPoint}
                  fillLinearGradientEndPoint={endPoint}
                  fillLinearGradientColorStops={flattenStops(el.stops)}
                />
              );
            }

            if (el.type === "shape") {
              return (
                <Rect
                  key={el.id}
                  {...commonProps(el)}
                  x={el.x}
                  y={el.y}
                  width={el.w}
                  height={el.h}
                  rotation={el.rotation ?? 0}
                  opacity={(el.opacity ?? 1) * (el.alpha ?? 1)}
                  fill={el.fill || "#000000"}
                  cornerRadius={el.radius ?? 0}
                />
              );
            }

            return null;
          })}

          <Transformer
            ref={transformerRef}
            keepRatio={selectedElement?.type === "logo"}
            shiftBehavior={selectedElement?.type === "logo" ? "inverted" : "default"}
            boundBoxFunc={(oldBox, newBox) =>
              newBox.width < 10 || newBox.height < 10 ? oldBox : newBox
            }
          />
        </Layer>
      </Stage>
    </CanvasWrapper>
  );
}

/** Logo node as a separate component so it can use hooks */
function LogoNode({ el, commonProps }) {
  const img = useLoadedImage(el._previewUrl);
  if (!img) {
    return (
      <Rect
        {...commonProps}
        x={el.x}
        y={el.y}
        width={el.w}
        height={el.h}
        rotation={el.rotation ?? 0}
        opacity={el.opacity ?? 1}
        fill="#cccccc"
        stroke="#888888"
        strokeWidth={2}
        dash={[10, 5]}
      />
    );
  }
  return (
    <KonvaImage
      {...commonProps}
      x={el.x}
      y={el.y}
      width={el.w}
      height={el.h}
      rotation={el.rotation ?? 0}
      opacity={el.opacity ?? 1}
      image={img}
    />
  );
}
